// Additional (non-headline) EBG services, kept from the original service pages.
import { ShieldCheck, Home as HomeIcon, Layers, KeyRound } from "lucide-react";

const RAW = {
    "property-advisory": {
        icon: HomeIcon, hero: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=2000&q=80",
        eyebrow: "Advisory",
        title: "Property Advisory",
        subtitle: "Bespoke recommendations tailored to your goals, lifestyle and long-term plans.",
        intro: "Whether it's your first home or your fifth investment, we sit across the table from you — not from the developer. We combine data, local market intel and 20 years of transaction memory to shortlist projects that genuinely fit your brief.",
        benefits: [
            "Written brief with your goals, budget, timelines and non-negotiables.",
            "Objective shortlist of 5-7 projects across your target micro-markets.",
            "Comparative worksheet — carpet area, ₹/sq.ft, appreciation, rental yield.",
            "Site walkthroughs with our senior advisor — never a salesperson.",
        ],
        steps: [
            { h: "Discovery Call", d: "45-minute video/office session to understand what you're solving for." },
            { h: "Shortlist Report", d: "Data-backed comparison of the top 5-7 projects that match your brief." },
            { h: "Guided Site Visits", d: "We walk you through the projects in person or on video — with the developer only present when you want them." },
            { h: "Negotiation Support", d: "We negotiate directly with the developer using our channel-partner leverage." },
            { h: "Handoff to Legal", d: "Once you decide, our legal team steps in without a single missed detail." },
        ],
        faqs: [
            { q: "Is there a fee for advisory?", a: "No. We are compensated by the developer per RERA norms — you pay nothing extra." },
            { q: "Do you push specific projects?", a: "We work with 240+ builders. Recommendations are outcome-led, not incentive-led." },
        ],
    },
    "legal-verification": {
        icon: ShieldCheck, hero: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=2000&q=80",
        eyebrow: "Legal First",
        title: "Legal Verification",
        subtitle: "A 120-point independent legal audit before you sign a single document.",
        intro: "We treat every property as if we were buying it ourselves. Our in-house legal team runs the same due diligence we'd want done on our own home — title chain, encumbrance, RERA registration, sanctioned plan, occupancy certificate, society NOCs and 100+ more checkpoints.",
        benefits: [
            "Mother deed, chain of title and encumbrance certificate reviewed for the last 30 years.",
            "RERA registration and complaint history verified on state portals.",
            "Sanctioned plan cross-verified against actual construction on site.",
            "Written 'Legally Verified' report attached to your file — signed by an advocate on record.",
        ],
        steps: [
            { h: "Document Requisition", d: "Standard 27-document list requested from the developer/seller." },
            { h: "Chain of Title", d: "Government records searched for every transfer in the last three decades." },
            { h: "Approvals Audit", d: "Sanctioned plan, occupancy certificate, environmental clearance." },
            { h: "Physical Verification", d: "Our surveyor cross-checks documents against the built structure." },
            { h: "Legal Opinion", d: "Signed opinion sent to you within 5 business days." },
        ],
        faqs: [
            { q: "Is this different from what a lawyer would do?", a: "It is a lawyer opinion — with a real-estate-specific 120-point checklist that generic advocates rarely run in full." },
            { q: "Does it cover under-construction projects?", a: "Yes — we monitor the project through possession, flagging every deviation." },
        ],
    },
    "project-management": {
        icon: Layers, hero: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=2000&q=80",
        eyebrow: "Turn-Key",
        title: "Project Management",
        subtitle: "Single point of accountability across architects, contractors, vendors — from ground-breaking to hand-over.",
        intro: "Building your own home or a commercial fit-out often turns into a decade of phone calls chasing 15 different vendors. EBG Project Management gives you a single Gantt chart, a single WhatsApp group and a single number to call.",
        benefits: [
            "PMO with weekly progress reports, drone footage and cash-flow tracker.",
            "Vetted panel of architects, civil, MEP and finishing contractors.",
            "Change-order and RFI process with 48-hour turnaround.",
            "Third-party quality audits at every RCC and finishing milestone.",
        ],
        steps: [
            { h: "Kick-Off & Scope", d: "Detailed brief, budget, timelines and quality bar signed off in Week 1." },
            { h: "Design Coordination", d: "We drive the architect + MEP consultant to a construction-ready package." },
            { h: "Contractor Onboarding", d: "Tenders floated across 5-7 short-listed contractors; awarded on price + delivery track record." },
            { h: "Site Supervision", d: "Daily site engineer, weekly walkthrough with owner and monthly steering committee." },
            { h: "Snag & Handover", d: "Full snag list closed before you sign the completion certificate." },
        ],
        faqs: [
            { q: "How is PM priced?", a: "Fixed % of project cost — typically 4-6% inclusive of PMO cost, quality audits and expediting." },
            { q: "Can you take over a stuck project?", a: "Yes. We audit the site, isolate the blocker (usually funds or vendor churn), and re-baseline the schedule." },
        ],
    },
    "property-management": {
        icon: KeyRound, hero: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=2000&q=80",
        eyebrow: "Own Peacefully",
        title: "Property Management",
        subtitle: "Rent collection, tenant vetting, maintenance and annual health checks — especially for NRI landlords.",
        intro: "Owning a rental property from 4,000 km away is not fun — leaks, late rent, terrible tenants, missed maintenance. Our property management wing acts like your local super-owner: monthly visits, quarterly audits, and a real-time dashboard of your asset.",
        benefits: [
            "Guaranteed rent — first-of-month deposit into your NRE/NRO account.",
            "Tenant vetting including background, employment and reference check.",
            "Monthly maintenance visits with photo report.",
            "Annual health check — plumbing, electricals, paint touch-ups, pest control.",
        ],
        steps: [
            { h: "Onboarding", d: "Property audit, photography and rental valuation." },
            { h: "Tenant Sourcing", d: "3-5 vetted tenants shortlisted within 30 days." },
            { h: "Agreement & Move-In", d: "Registered lease, security deposit escrow, key handover." },
            { h: "Ongoing", d: "Monthly rent collection, quarterly walkthrough, annual health check." },
            { h: "Exit", d: "Full move-out audit with security-deposit adjustment." },
        ],
        faqs: [
            { q: "What is the fee?", a: "1 month's rent per year of tenancy + 10% on new tenant onboarding. Fully transparent." },
            { q: "Do you handle short-stay rentals?", a: "For select premium properties, yes. Ask us about Airbnb-managed inventory in Mumbai, Bengaluru and Goa." },
        ],
    },
};

const CARD = {
    "property-advisory": { d: "Personalised recommendations based on your goals, lifestyle & investment plans.", img: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1400&q=80" },
    "legal-verification": { d: "120+ point legal due diligence for ownership, title, encumbrances, RERA & approvals.", img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&q=80" },
    "project-management": { d: "Turn-key coordination across architects, contractors & vendors for a friction-free build.", img: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1400&q=80" },
    "property-management": { d: "Rent collection, tenant vetting, maintenance & annual health-checks for NRI landlords.", img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1400&q=80" },
};

export const EXTRA_SERVICES = Object.entries(RAW).map(([slug, c]) => ({
    ...c, slug, primary: false, intent: "buy", t: c.title, d: CARD[slug].d, short: CARD[slug].d, img: CARD[slug].img,
}));
